import { format } from 'date-fns';
import type { DayAgendaItem } from '@/lib/calendarAgenda';
import { cn } from '@/lib/cn';
import { PMS_APP_URL } from '@/services/pmsApi';
import { CALENDAR_KIND_CHIP_CLASS, CALENDAR_SURFACE_CLASS } from '@/components/calendar/shared/calendarTheme';

type Props = {
  item: DayAgendaItem | null;
  date: Date | null;
  onClose: () => void;
  onDeleteNote?: (id: string) => void;
  isDeletingNote?: boolean;
};

const KIND_STYLES: Record<DayAgendaItem['kind'], { dot: string; chip: string; label: string }> = {
  event: { dot: 'bg-blue-500', chip: CALENDAR_KIND_CHIP_CLASS.event, label: 'Event' },
  meeting: { dot: 'bg-fuchsia-500', chip: CALENDAR_KIND_CHIP_CLASS.meeting, label: 'Meeting' },
  'org-event': { dot: 'bg-violet-500', chip: CALENDAR_KIND_CHIP_CLASS['org-event'], label: 'Event' },
  holiday: { dot: 'bg-emerald-500', chip: CALENDAR_KIND_CHIP_CLASS.holiday, label: 'Organization holiday' },
  task: { dot: 'bg-orange-500', chip: CALENDAR_KIND_CHIP_CLASS.task, label: 'Task' },
  note: { dot: 'bg-sky-500', chip: CALENDAR_KIND_CHIP_CLASS.note, label: 'Note' },
  schedule: { dot: 'bg-slate-500', chip: CALENDAR_KIND_CHIP_CLASS.schedule, label: 'Schedule' },
  'pms-task': { dot: 'bg-violet-500', chip: CALENDAR_KIND_CHIP_CLASS['pms-task'], label: 'PMS Task' },
  'pms-project': { dot: 'bg-cyan-500', chip: CALENDAR_KIND_CHIP_CLASS['pms-project'], label: 'PMS Project' },
};

export function CalendarItemPopup({ item, date, onClose, onDeleteNote, isDeletingNote }: Props) {
  if (!item || !date) return null;

  const canDeleteNote = item.kind === 'note' && Boolean(onDeleteNote);
  const handleDeleteNote = () => {
    if (!onDeleteNote || isDeletingNote) return;
    if (!window.confirm(`Delete note "${item.title}"?`)) return;
    onDeleteNote(item.id);
  };

  const kind = KIND_STYLES[item.kind];
  const MEET_APP_URL = String(import.meta.env.VITE_MEET_APP_URL || '').replace(/\/+$/, '');
  const meetingRoomId = item.meetingMeta?.roomId ?? (item.kind === 'meeting' ? item.id : null);
  const joinUrl = MEET_APP_URL && meetingRoomId ? `${MEET_APP_URL}/r/${meetingRoomId}` : null;
  const meetHomeUrl = MEET_APP_URL || null;
  const pmsUrl =
    PMS_APP_URL && item.kind === 'pms-task'
      ? `${PMS_APP_URL}/tasks/${item.id.replace(/^pms-task-/, '')}`
      : PMS_APP_URL && item.kind === 'pms-project'
        ? `${PMS_APP_URL}/projects/${item.id.replace(/^pms-project-/, '')}`
        : null;

  const handleCopyMeetingLink = async () => {
    if (!joinUrl) return;
    try { await navigator.clipboard.writeText(joinUrl); } catch { /* ignore */ }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/45 backdrop-blur-md p-3 sm:p-4 animate-in fade-in duration-300 ease-out">
      <button type="button" aria-label="Close item details" className="absolute inset-0 h-full w-full cursor-default" onClick={onClose} />

      <article className={CALENDAR_SURFACE_CLASS.popupCard}>
        <header className="mb-6 flex items-start justify-between gap-4 sm:gap-6">
          <div className="flex-1">
            <p className="mb-1 text-xs font-bold uppercase tracking-widest text-muted-foreground">
              {format(date, 'EEEE, MMM d, yyyy')}
            </p>
            <h2 className="text-xl font-bold leading-tight tracking-tight text-foreground sm:text-2xl">{item.title}</h2>
          </div>
          <button type="button" onClick={onClose} className={CALENDAR_SURFACE_CLASS.iconButton}>
            <CloseIcon />
          </button>
        </header>

        <div className="mb-6 flex flex-wrap items-center gap-3">
          <span className={cn('rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-widest', kind.chip)}>
            {kind.label}
          </span>
          <span className="flex items-center gap-1.5 text-xs font-semibold text-muted-foreground">
            <ClockIcon />
            {item.timeLabel}
          </span>
        </div>

        {item.kind === 'event' && item.remindersSummary ? (
          <p className={cn('mb-3', CALENDAR_SURFACE_CLASS.metaText)}>{item.remindersSummary}</p>
        ) : null}

        {item.details && (
          <div className={CALENDAR_SURFACE_CLASS.subtlePanel}>
            <p className="whitespace-pre-wrap text-sm font-medium leading-relaxed text-foreground">{item.details}</p>
          </div>
        )}

        {item.kind === 'meeting' ? (
          <div className="mt-4 space-y-3">
            <div className={cn(CALENDAR_SURFACE_CLASS.subtlePanel, 'text-sm text-foreground')}>
              <p><span className="font-semibold">Room ID:</span> {meetingRoomId ?? 'N/A'}</p>
              <p><span className="font-semibold">Status:</span> {item.meetingMeta?.status ?? 'scheduled'}</p>
              <p><span className="font-semibold">Duration:</span> {item.meetingMeta?.durationMinutes ?? 60} min</p>
            </div>
            <div className="flex flex-wrap gap-2">
              {joinUrl ? (
                <a href={joinUrl} target="_blank" rel="noopener noreferrer" className="rounded-xl bg-brand px-4 py-2 text-sm font-semibold text-brand-foreground shadow-sm transition-all hover:opacity-90 hover:shadow">
                  Join meeting
                </a>
              ) : null}
              {meetHomeUrl ? (
                <a href={meetHomeUrl} target="_blank" rel="noopener noreferrer" className={CALENDAR_SURFACE_CLASS.outlineButton}>
                  Open Meet
                </a>
              ) : null}
              {joinUrl ? (
                <button type="button" onClick={handleCopyMeetingLink} className={CALENDAR_SURFACE_CLASS.outlineButton}>
                  Copy link
                </button>
              ) : null}
            </div>
          </div>
        ) : null}

        {pmsUrl ? (
          <div className="mt-4 flex flex-wrap gap-2">
            <a href={pmsUrl} target="_blank" rel="noopener noreferrer" className="rounded-xl bg-brand px-4 py-2 text-sm font-semibold text-brand-foreground shadow-sm transition-all hover:opacity-90 hover:shadow">
              Open in PMS
            </a>
          </div>
        ) : null}

        {canDeleteNote ? (
          <div className="mt-6 flex justify-end border-t border-border pt-4">
            <button
              type="button"
              onClick={handleDeleteNote}
              disabled={isDeletingNote}
              className="inline-flex items-center gap-2 rounded-xl border border-destructive/30 px-4 py-2 text-sm font-semibold text-destructive transition-colors hover:bg-destructive/10 disabled:opacity-60"
            >
              {isDeletingNote ? <SpinnerIcon /> : <TrashIcon />}
              {isDeletingNote ? 'Deleting…' : 'Delete note'}
            </button>
          </div>
        ) : null}
      </article>
    </div>
  );
}

function CloseIcon() {
  return <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>;
}

function ClockIcon() {
  return <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>;
}

function TrashIcon() {
  return <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>;
}

function SpinnerIcon() {
  return <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>;
}
